// components/ProfChatPanel.tsx — V28 C3: shared Il Prof chat body.
//
// A self-contained chat panel that renders an Il Prof conversation. By default
// it reads the SAME Zustand store thread as ProfScreen (`useChat`, thread id
// 'chiedi', `chiediSessionId`) and sends through the SAME `chiediStream` flow —
// so /prof and the global slide-up sheet (GlobalChatSheet, C3) show the SAME
// live conversation, not a parallel one. The store survives unmount, so opening
// the sheet from another screen picks up exactly where /prof left off.
//
// R2-13 (founder LAW): the panel is now PARAMETERIZED by `threadId`. The global
// Prof uses the persisted 'chiedi' thread (default). The IN-LESSON Prof chat is
// CONFINED to its lesson — GlobalChatSheet passes a lesson-scoped FE thread id
// (`lesson:<microTopicId>` / `lesson:<argomentoId>`) AND `persistSession={false}`
// so the lesson conversation:
//   (a) reads/writes a SEPARATE store thread (never the global 'chiedi' history), and
//   (b) sends NO chiediSessionId — the BE starts a fresh per-open server thread,
//       so the lesson Prof can never resurface or append to the global thread.
// The main Prof and the lesson Prof are NOT connected.
//
// Scope (blueprint v28-fe-build-blueprint.md §C3): "Extract ProfScreen's chat
// body (thread + Composer + handleSend / chiediStream wiring) into a shared
// panel." The realtime voice (Qwen call) stays owned by the host surfaces
// (ProfScreen today; VoiceCallScreen in C4) — this panel exposes an optional
// `onCall` prop it forwards to the Composer so a host can wire that entry point
// without the panel owning the logic. The mic always DICTATES into the text
// field (it-IT Web-Speech owned by the Composer); no onMic is threaded. The
// swap of ProfScreen's inline body to <ProfChatPanel> is a LATER surface step
// (S1) and is intentionally NOT done here.
//
// Persisted identifiers (thread id 'chiedi', `chiediSessionId`, the
// `asc_chat_v1` localStorage shape) are read through the existing store and are
// left unchanged for the default global thread.

import type { JSX } from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatThread, type ProfFeedbackSignal } from './ChatThread';
import { ActionChips } from './ActionChips';
import { Composer } from './Composer';
import { ProfFigureContext } from './ProfFigureContext';
import { getToken } from '../api/client';
import { prepareProfFigureTurn, profFigureSessionId, receiveProfFigureEvent, receiveProfFigureError } from '../state/prof-figure';
import { useChat, isScopedThreadId } from '../state/chat';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import { useTokens } from '../state/theme';
import { useSpeechSynthesis } from '../utils/useSpeechSynthesis';
import { chiediStream } from '../api/tutor';
import { profFeedbackApi } from '../api/prof-feedback';
import {
  profCheckpointApi,
  type ProfCheckpointEncounter,
  type ProfCheckpointResult,
} from '../api/prof-checkpoint';
import type { ChiediHistoryMessage } from '../api/tutor';
import type { Message } from '../types/domain';
import type { ProfCitationV1 } from '../types/prof-citation';
import { ProfCheckpointCard } from './ProfCheckpointCard';

// FP7 §1 (Mode B) — the spoken fallback when the model didn't fill CTRL `spoken`:
// the first paragraph of the answer (markers stripped, capped). Mirrors ProfScreen.
function firstParagraph(text: string, maxChars = 300): string {
  const firstBlock = text.split(/\n\s*\n/)[0] ?? text;
  const clean = firstBlock
    .replace(/\[\[image:[^\]]+\]\]/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.length <= maxChars ? clean : clean.slice(0, maxChars).replace(/\s+\S*$/, '');
}

function latestProfText(messages: Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.authorKind === 'prof' && 'text' in m && m.text.trim()) return m.text.trim();
  }
  return null;
}

const DEFAULT_THREAD_ID = 'chiedi';
const EMPTY_MESSAGES: Message[] = [];

type CheckpointUiState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      encounter: ProfCheckpointEncounter;
      idempotencyKey: string;
      sessionId: string;
    }
  /** OA1 — the server graded a typed/dictated answer (checkpoint_result SSE);
   *  the card renders already complete under the explanation turn. */
  | {
      kind: 'graded';
      result: ProfCheckpointResult;
      questionText: string | null;
    };

export interface ProfChatPanelProps {
  /**
   * Compact chrome for tight hosts (the slide-up sheet). Trims the thread's
   * top padding and the chip gutters so the conversation sits flush under the
   * sheet head. Defaults to false (the roomy /prof layout).
   */
  compact?: boolean;
  /** Composer placeholder copy. Defaults to the standard Il Prof prompt. */
  placeholder?: string;
  /**
   * R2-13: which store thread this panel reads/writes. Defaults to 'chiedi'
   * (the persisted global Prof conversation shown on /prof and the global
   * sheet). The in-lesson Prof passes a lesson-scoped id (`lesson:<id>`) so it
   * renders a SEPARATE conversation that never touches the global thread.
   */
  threadId?: string;
  /**
   * Whether to reuse/persist a BE-issued chat-thread session id across turns.
   * Defaults to true.
   *
   * - GLOBAL thread ('chiedi'): persists the global `chiediSessionId` (the main
   *   Prof keeps writing into the same server-side thread across opens).
   * - SCOPED thread (`lesson:*` / `appendice-*:*`, Item B): persists a PER-THREAD
   *   session id in `scopedSessionIds[threadId]`, entirely separate from the
   *   global `chiediSessionId`. This is what makes the founder contract work: the
   *   SAME visit (same threadId) keeps the same BE session ("it keeps going"),
   *   the BE persists it as a mode='chiedi' session so it appears in Cronologia,
   *   and a fresh visit (new nonce → new threadId) starts a brand-new session.
   *
   * Pass false to send NO sessionId at all (a fully ephemeral thread).
   */
  persistSession?: boolean;
  /**
   * Call-icon handler forwarded to the Composer (e.g. host → voice-call
   * surface, C4). The call icon only renders when this is provided. The mic is
   * not configurable — it always dictates (it-IT Web-Speech owned by Composer).
   */
  onCall?: () => void;
  /**
   * Called after a student message is successfully sent. Hosts use this to,
   * e.g., scroll the sheet body or close an empty-state hint. Optional.
   */
  onSent?: (text: string) => void;
  /**
   * Item 2 (founder round-3, 2026-06-11) — when this panel is the IN-LESSON Prof
   * chat, the student's current lesson position. Forwarded to /api/chiedi so the
   * FULL Prof answers WITH a lesson BIAS (anchored, never refuses a driving/exam
   * question), not lesson-LIMITED. Absent on the global /prof thread.
   */
  lessonContext?: {
    argomentoId: string;
    argomentoName?: string;
    lessonTitle?: string;
    chapterTitle?: string;
    chapterOrder?: number;
    chapterCount?: number;
    visibleAssetRefs?: string[];
    questionRefs?: string[];
  };
}

export function ProfChatPanel({
  compact = false,
  placeholder,
  threadId = DEFAULT_THREAD_ID,
  persistSession = true,
  onCall,
  onSent,
  lessonContext,
}: ProfChatPanelProps): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const THREAD_ID = threadId;
  const scoped = isScopedThreadId(THREAD_ID);
  // FP7 §1 — speak the CTRL `spoken` summary for a hold-to-talk voice note.
  const { supported: speechSupported, speak, stop: stopSpeech } = useSpeechSynthesis();

  // Same store; the thread id selects WHICH conversation. For the default
  // 'chiedi' thread this is the shared /prof + sheet conversation; for a
  // `lesson:*` thread it's a separate, lesson-only conversation (R2-13).
  const messages = useChat((s) => s.threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES);
  const isStreaming = useChat((s) => s.threads[THREAD_ID]?.streaming ?? false);
  const savingFigure = useChat((s) => s.pendingProfFiguresByThread[THREAD_ID]?.status === 'saving');
  const preparingFigureTurn = useRef(false);
  const durableSessionId = useChat((s) => !persistSession
    ? null
    : scoped
      ? (s.scopedSessionIds[THREAD_ID] ?? null)
      : s.chiediSessionId);

  // Inline principle expansion (Regola / citation) inside Prof bubbles.
  const expandedPrinciple = useChat((s) => s.expandedPrinciple);
  const expandPrinciple = useChat((s) => s.expandPrinciple);
  const collapsePrinciple = useChat((s) => s.collapsePrinciple);

  // Post-refusal / follow-up continuation chips, and the one-tap retry after a
  // network drop. Cleared on each new send. Local to the panel instance — the
  // chips are transient UI, not conversation state, so they don't belong in the
  // shared store (a stale chip in two mounted panels would be confusing).
  // OA4 host reconciliation — the GLOBAL thread's suggestion chips live in the
  // persisted store (same source ProfScreen reads, so they survive reload and
  // both hosts agree); SCOPED threads stay component-local (single-visit by
  // design, like scopedSessionIds).
  const [localSuggestions, setLocalSuggestions] = useState<string[] | null>(null);
  const storeSuggestions = useChat((s) => s.lastSuggestions);
  const usesStoreSuggestions = persistSession && !scoped;
  const lastSuggestions = usesStoreSuggestions ? storeSuggestions : localSuggestions;
  const setLastSuggestions = useCallback((suggestions: string[] | null) => {
    if (usesStoreSuggestions) useChat.getState().setLastSuggestions(suggestions);
    else setLocalSuggestions(suggestions);
  }, [usesStoreSuggestions]);
  // OA4 — FE-read flag: dictation review (D4 option B) + in-bubble recovery
  // actions. Off ⇒ auto-send + below-thread chips stay byte-identical.
  const oa4ReviewEnabled = useFeatureFlag('oa4_dictation_review_enabled');
  const [retryQuestion, setRetryQuestion] = useState<string | null>(null);
  const [profFeedbackByMessageId, setProfFeedbackByMessageId] =
    useState<Record<string, ProfFeedbackSignal>>({});
  const [checkpointUiByMessageId, setCheckpointUiByMessageId] =
    useState<Record<string, CheckpointUiState>>({});
  const profFeedbackQueuesRef = useRef<Record<string, Promise<void>>>({});
  const profFeedbackRevisionsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!durableSessionId) {
      setProfFeedbackByMessageId({});
      return;
    }
    let alive = true;
    profFeedbackApi.list(durableSessionId)
      .then(({ feedback }) => {
        if (alive) setProfFeedbackByMessageId(Object.fromEntries(feedback.map((row) => [row.messageId, row.signal])));
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [durableSessionId]);

  useEffect(() => {
    setCheckpointUiByMessageId({});
  }, [THREAD_ID]);

  const handleSend = useCallback(
    async (text: string, opts?: { voiceNote?: boolean }) => {
      const clean = text.trim();
      if (!clean) return;
      if (useChat.getState().threads[THREAD_ID]?.streaming || preparingFigureTurn.current) return;
      const figureToken = getToken();
      const figureSession = profFigureSessionId(THREAD_ID);
      preparingFigureTurn.current = true;
      let figureTurn: Awaited<ReturnType<typeof prepareProfFigureTurn>> = { ok: true };
      if (persistSession && useChat.getState().pendingProfFiguresByThread[THREAD_ID]) {
        figureTurn = await prepareProfFigureTurn(THREAD_ID);
      }
      preparingFigureTurn.current = false;
      if (!figureTurn.ok || getToken() !== figureToken || profFigureSessionId(THREAD_ID) !== figureSession) return;
      // FP7 §1 (Mode B): a hold-to-talk voice note rides the normal text path with
      // voiceNote=true so the BE returns a CTRL `spoken` summary we speak aloud.
      const isVoiceNote = opts?.voiceNote === true;
      if (speechSupported) stopSpeech();

      // Clear any post-refusal suggestions and retry state — new exchange.
      setLastSuggestions(null);
      setRetryQuestion(null);

      // 1. Collect history (last 10 messages) BEFORE appending the new one.
      const currentMessages = useChat.getState().threads[THREAD_ID]?.messages ?? [];
      const historyMessages = currentMessages.slice(-10);
      const history: ChiediHistoryMessage[] = historyMessages
        .filter((m) => 'text' in m && (m.authorKind === 'you' || m.authorKind === 'prof'))
        .map((m) => ({
          role: m.authorKind === 'you' ? ('user' as const) : ('prof' as const),
          text: (m as { text: string }).text,
        }));

      // 2. Append the student's message.
      useChat.getState().appendMessage(THREAD_ID, {
        id: crypto.randomUUID(),
        authorKind: 'you',
        kind: 'text',
        text: clean,
      });

      // 3. Append the streaming placeholder ("Il Prof sta scrivendo…").
      const responseMessageId = crypto.randomUUID();
      useChat.getState().appendMessage(THREAD_ID, {
        id: responseMessageId,
        authorKind: 'prof',
        kind: 'streaming',
        text: '',
        feedbackEligible: false,
      });
      useChat.getState().setStreaming(THREAD_ID, true);

      onSent?.(clean);

      // 4. Stream. Reuse the BE-issued chat-thread session id (if any) so the BE
      //    persists turns into the SAME chiedi_messages thread for the duration
      //    of this thread:
      //      - GLOBAL ('chiedi'): reads `chiediSessionId`.
      //      - SCOPED (lesson / sub-argomento, Item B): reads the per-thread
      //        `scopedSessionIds[threadId]` — its OWN session, never the global
      //        one, so the scoped Prof can't read from or append to the global
      //        conversation, AND its turns accumulate into one BE session per
      //        visit (which then shows in Cronologia).
      //    On the very first message it's null — the BE creates one and emits it
      //    via the `session` SSE event, which we cache below.
      //    `persistSession=false` sends NO sessionId (fully ephemeral thread).
      const cachedSessionId = !persistSession
        ? null
        : scoped
          ? (useChat.getState().scopedSessionIds[THREAD_ID] ?? null)
          : useChat.getState().chiediSessionId;
      let responseSessionId = cachedSessionId;
      // Item 2 — attach the lesson context ONLY for a scoped (lesson) thread, so
      // the in-lesson Prof answers with a lesson bias. The global 'chiedi' thread
      // never carries it (it would wrongly suppress the off-topic boundary there).
      const lessonContextForTurn = scoped && lessonContext ? lessonContext : undefined;
      try {
        await chiediStream(
          {
            question: clean,
            ...('command' in figureTurn && figureTurn.command ? { figureContext: figureTurn.command } : {}),
            ...(persistSession ? { responseId: responseMessageId } : {}),
            history,
            sessionId: cachedSessionId ?? undefined,
            ...(isVoiceNote ? { voiceNote: true as const } : {}),
            ...(lessonContextForTurn ? { lessonContext: lessonContextForTurn } : {}),
          },
          {
            onFigureContext: (event) => {
              if (getToken() === figureToken) receiveProfFigureEvent(THREAD_ID, event);
            },
            onSession: (id) => {
              responseSessionId = id;
              // `persistSession=false` → drop the BE session id entirely.
              if (!persistSession) return;
              if (scoped) {
                // Item B — cache the BE session id for THIS scoped thread only
                // (never the global chiediSessionId). Defensive: only take
                // ownership when this thread doesn't already have one.
                const current = useChat.getState().scopedSessionIds[THREAD_ID];
                if (!current) {
                  useChat.getState().setScopedSessionId(THREAD_ID, id);
                }
                return;
              }
              // Global thread: cache into chiediSessionId. Defensive: only take
              // ownership when we don't already have one.
              const current = useChat.getState().chiediSessionId;
              if (!current) {
                useChat.getState().setChiediSessionId(id);
              }
            },
            onToken: (chunk) => {
              useChat.getState().appendToLastMessage(THREAD_ID, chunk);
            },
            onReplaceText: (fullText) => {
              useChat.getState().replaceLastMessageText(THREAD_ID, fullText);
            },
            onCitations: (citations) => {
              useChat.getState().setCitationsOnLast(THREAD_ID, citations);
            },
            onFollowUps: (questions) => {
              // Always offer "spiegamelo più semplice" alongside the contextual
              // follow-ups so the student has a low-effort next step.
              const simplify = 'Puoi spiegarmelo in modo più semplice?';
              const dedup = questions.filter((q) => q !== simplify);
              setLastSuggestions([...dedup, simplify]);
            },
            // OA1 — server auto-issued encounter for this answer: render the
            // V/F card under the turn without any student tap.
            onCheckpoint: (encounter) => {
              if (!responseSessionId) return;
              const sessionIdForCard = responseSessionId;
              setCheckpointUiByMessageId((current) => ({
                ...current,
                [responseMessageId]: {
                  kind: 'ready',
                  encounter,
                  idempotencyKey: crypto.randomUUID(),
                  sessionId: sessionIdForCard,
                },
              }));
            },
            // OA1 — the server graded a typed/dictated answer exact-once: the
            // card renders already complete under the explanation.
            onCheckpointResult: (result, questionText) => {
              setCheckpointUiByMessageId((current) => ({
                ...current,
                [responseMessageId]: { kind: 'graded', result, questionText },
              }));
            },
            onDone: (done) => {
              useChat.getState().finalizeStreamingMessage(THREAD_ID);
              const persistenceOutcome = done?.assistant_persistence_outcome
                ?? (done?.response_persisted === true ? 'persisted' : undefined);
              if (done?.response_id === responseMessageId && persistenceOutcome) {
                useChat.getState().setMessageAssistantPersistenceOutcome(
                  THREAD_ID,
                  responseMessageId,
                  persistenceOutcome,
                );
              }
              const durablyPersisted = persistenceOutcome === 'persisted'
                || persistenceOutcome === 'recovered_duplicate';
              if (persistSession && durablyPersisted && done?.response_id === responseMessageId) {
                useChat.getState().setMessageFeedbackEligible(THREAD_ID, responseMessageId, true);
                if (responseSessionId) {
                  useChat.getState().setMessageCheckpointSessionId(
                    THREAD_ID,
                    responseMessageId,
                    responseSessionId,
                  );
                }
              }
              useChat.getState().setStreaming(THREAD_ID, false);
              // FP7 §1 (Mode B): for a voice note, speak the CTRL `spoken` summary
              // (≤300 chars) while the full text stays on screen; fall back to the
              // first paragraph of the latest answer when the model didn't fill it.
              if (isVoiceNote && speechSupported) {
                const summary = done?.spoken?.trim();
                const fallback = firstParagraph(
                  latestProfText(useChat.getState().threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES) ?? '',
                );
                const toSpeak = summary || fallback;
                if (toSpeak) speak(toSpeak, { lang: 'it-IT' });
              }
            },
            onRefused: (reason, message, suggestions) => {
              // Honor the BE contract for session_not_found: drop the cached
              // sessionId so the next student message starts a fresh thread —
              // for the global thread (chiediSessionId) OR a scoped thread
              // (its per-thread scopedSessionIds entry, Item B).
              if (reason === 'session_not_found' && persistSession) {
                if (scoped) useChat.getState().setScopedSessionId(THREAD_ID, null);
                else useChat.getState().setChiediSessionId(null);
              }
              useChat.getState().removeStreamingPlaceholder(THREAD_ID);
              useChat.getState().appendMessage(THREAD_ID, {
                id: crypto.randomUUID(),
                authorKind: 'prof',
                kind: 'text',
                text: message || 'Non ho una risposta sicura per questa. Prova a riformulare.',
              });
              useChat.getState().setStreaming(THREAD_ID, false);
              if (suggestions && suggestions.length > 0) {
                setLastSuggestions(suggestions);
              }
            },
            onError: (error) => {
              if (getToken() === figureToken
                && useChat.getState().threads[THREAD_ID]?.messages.some((message) => message.id === responseMessageId)
                && receiveProfFigureError(THREAD_ID, error)) {
                useChat.getState().removeStreamingPlaceholder(THREAD_ID);
                useChat.getState().setStreaming(THREAD_ID, false);
                setRetryQuestion(clean);
                return;
              }
              useChat.getState().removeStreamingPlaceholder(THREAD_ID);
              useChat.getState().appendMessage(THREAD_ID, {
                id: crypto.randomUUID(),
                authorKind: 'prof',
                kind: 'text',
                text: 'Si è interrotta la connessione. Riprova tra qualche secondo.',
              });
              useChat.getState().setStreaming(THREAD_ID, false);
              setRetryQuestion(clean);
            },
          },
        );
      } catch {
        useChat.getState().removeStreamingPlaceholder(THREAD_ID);
        useChat.getState().appendMessage(THREAD_ID, {
          id: crypto.randomUUID(),
          authorKind: 'prof',
          kind: 'text',
          text: 'Connessione interrotta. Riprova tra qualche secondo.',
        });
        useChat.getState().setStreaming(THREAD_ID, false);
        setRetryQuestion(clean);
      }
    },
    [onSent, THREAD_ID, persistSession, speechSupported, speak, stopSpeech, lessonContext],
  );

  const handleVoiceNote = useCallback(
    (text: string) => { void handleSend(text, { voiceNote: true }); },
    [handleSend],
  );

  const handleOpenCitation = useCallback((citation: ProfCitationV1) => {
    const { argomentoId, principleId } = citation.destination;
    navigate(`/appendice/${encodeURIComponent(argomentoId)}/${encodeURIComponent(principleId)}`);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    if (!retryQuestion) return;
    const q = retryQuestion;
    setRetryQuestion(null);
    void handleSend(q);
  }, [retryQuestion, handleSend]);

  const handleProfFeedback = useCallback(async (messageId: string, feedback: ProfFeedbackSignal | null) => {
    let previous: ProfFeedbackSignal | undefined;
    setProfFeedbackByMessageId((current) => {
      previous = current[messageId];
      const next = { ...current };
      if (feedback === null) delete next[messageId];
      else next[messageId] = feedback;
      return next;
    });
    const revision = (profFeedbackRevisionsRef.current[messageId] ?? 0) + 1;
    profFeedbackRevisionsRef.current[messageId] = revision;
    const previousWrite = profFeedbackQueuesRef.current[messageId] ?? Promise.resolve();
    const write = previousWrite
      .catch(() => undefined)
      .then(async () => {
        if (feedback === null) await profFeedbackApi.remove(messageId);
        else await profFeedbackApi.upsert(messageId, feedback);
      });
    profFeedbackQueuesRef.current[messageId] = write;
    try {
      await write;
    } catch {
      if (profFeedbackRevisionsRef.current[messageId] !== revision) return;
      setProfFeedbackByMessageId((current) => {
        const next = { ...current };
        if (previous === undefined) delete next[messageId];
        else next[messageId] = previous;
        return next;
      });
    } finally {
      if (profFeedbackQueuesRef.current[messageId] === write) delete profFeedbackQueuesRef.current[messageId];
    }
  }, []);

  const handleRequestCheckpoint = useCallback(async (messageId: string, sessionId: string) => {
    setCheckpointUiByMessageId((current) => ({
      ...current,
      [messageId]: { kind: 'pending' },
    }));
    try {
      const { encounter } = await profCheckpointApi.issue(sessionId, messageId);
      setCheckpointUiByMessageId((current) => ({
        ...current,
        [messageId]: {
          kind: 'ready',
          encounter,
          idempotencyKey: crypto.randomUUID(),
          sessionId,
        },
      }));
    } catch {
      setCheckpointUiByMessageId((current) => ({
        ...current,
        [messageId]: { kind: 'error' },
      }));
    }
  }, []);

  const checkpointByMessageId = Object.fromEntries(
    Object.entries(checkpointUiByMessageId).flatMap(([messageId, state]) => {
      if (state.kind === 'ready') {
        return [[
          messageId,
          <ProfCheckpointCard
            key={state.encounter.id}
            encounter={state.encounter}
            onRefresh={() => { void handleRequestCheckpoint(messageId, state.sessionId); }}
            onSubmit={async (answer) => {
              const { result } = await profCheckpointApi.submit(
                state.encounter.id,
                state.idempotencyKey,
                answer,
              );
              return result;
            }}
          />,
        ]];
      }
      // OA1 — server-graded chat answer: already complete, no submit path.
      if (state.kind === 'graded' && state.questionText) {
        return [[
          messageId,
          <ProfCheckpointCard
            key={state.result.encounterId}
            encounter={{
              id: state.result.encounterId,
              questionId: state.result.encounterId,
              questionText: state.questionText,
              expiresAt: state.result.completedAt,
            }}
            initialResult={state.result}
            onRefresh={() => undefined}
            onSubmit={() => Promise.resolve(state.result)}
          />,
        ]];
      }
      return [];
    }),
  );
  const checkpointIssueStateByMessageId = Object.fromEntries(
    Object.entries(checkpointUiByMessageId).flatMap(([messageId, state]) => (
      state.kind === 'pending' || state.kind === 'error'
        ? [[messageId, state.kind]]
        : []
    )),
  ) as Record<string, 'pending' | 'error'>;

  const chipGutter = compact ? '0 12px 8px' : '0 16px 8px';

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // Light-only (V28 F1): tokens.mode always resolves 'light'. Kept off the
        // token so a host without its own background still reads correctly.
        background: tokens.bg,
        color: tokens.text,
      }}
    >
      <ChatThread
        messages={messages}
        expandedPrinciple={expandedPrinciple}
        onExpandPrinciple={expandPrinciple}
        onCollapsePrinciple={collapsePrinciple}
        onOpenCitation={handleOpenCitation}
        resolvedPrinciple={null}
        feedbackByMessageId={profFeedbackByMessageId}
        onProfFeedback={handleProfFeedback}
        onRequestCheckpoint={handleRequestCheckpoint}
        checkpointByMessageId={checkpointByMessageId}
        checkpointIssueStateByMessageId={checkpointIssueStateByMessageId}
        {...(oa4ReviewEnabled && lastSuggestions && lastSuggestions.length > 0
          ? { followUps: lastSuggestions, onFollowUp: (q: string) => { void handleSend(q); } }
          : {})}
      />

      {/* Retry chip — after a network error, so the student doesn't retype.
          One-shot: cleared on click or on the next send. */}
      {retryQuestion && !isStreaming && (
        <div style={{ padding: chipGutter, flexShrink: 0 }}>
          <ActionChips
            actions={[{ kind: 'primary', label: 'Riprova ↻', onClick: handleRetry }]}
          />
        </div>
      )}

      {/* Post-refusal / follow-up suggestions — keep the chat alive. */}
      {!oa4ReviewEnabled && lastSuggestions && lastSuggestions.length > 0 && !isStreaming && (
        <div style={{ padding: chipGutter, flexShrink: 0 }}>
          <ActionChips
            actions={lastSuggestions.map((s) => ({
              label: s,
              onClick: () => void handleSend(s),
            }))}
          />
        </div>
      )}

      <ProfFigureContext threadId={THREAD_ID} />
      <Composer
        placeholder={placeholder ?? 'Scrivi al Prof…'}
        disabled={isStreaming || savingFigure}
        voiceMode={false}
        onSend={(t) => void handleSend(t)}
        onVoiceNote={handleVoiceNote}
        voiceNoteReview={oa4ReviewEnabled}
        {...(onCall ? { onCall } : {})}
      />
    </div>
  );
}

export default ProfChatPanel;
